import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Check, AlertCircle } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OptOutPage() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !agree) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/optout`, { email: email.trim().toLowerCase(), reason });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit your request. Please email admin@buyerbeacon.pro.";
      setError(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="optout-page">
      <SEO
        title="Opt Out of Marketing Data — BuyerBeacon Pro"
        description="Remove your personal information from BuyerBeacon Pro's marketing database. Submissions processed within 15 business days. CCPA & state-privacy compliant."
        path="/optout"
        noIndex
      />
      <Navbar />
      <main className="pt-28 pb-20">
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute left-1/2 top-[40px] -z-10 h-[280px] w-[600px] -translate-x-1/2 rounded-full bg-[#d4af37]/10 blur-[120px]" />
          <div className="mx-auto max-w-2xl px-5 lg:px-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#1e1b4b]/[0.06] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#1e1b4b]">
              <ShieldCheck className="h-3.5 w-3.5 text-[#d4af37]" />
              Privacy Request
            </div>
            <h1 className="mt-4 text-4xl font-medium tracking-tight text-[#1e1b4b] sm:text-5xl">
              Opt out of marketing data
            </h1>
            <p className="mt-4 text-[16px] leading-relaxed text-slate-600">
              Submit your email to remove your personal information from
              BuyerBeacon Pro's marketing database. We'll process your request
              within 15 business days and send you a confirmation when complete.
            </p>

            {done ? (
              <div
                data-testid="optout-success"
                className="mt-10 rounded-2xl border border-green-200 bg-green-50 p-7"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-600 text-white">
                  <Check className="h-5 w-5" />
                </div>
                <h2 className="mt-4 text-xl font-semibold text-[#1e1b4b]">
                  Request received
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  We've logged your opt-out request and sent a confirmation
                  email. Your information will be removed from our active
                  marketing databases within 15 business days. If you have any
                  questions, contact{" "}
                  <a
                    href="mailto:admin@buyerbeacon.pro"
                    className="text-[#1e1b4b] underline-offset-4 hover:underline"
                  >
                    admin@buyerbeacon.pro
                  </a>
                  .
                </p>
                <Link
                  to="/"
                  className="mt-5 inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-[#1e1b4b] hover:bg-slate-50"
                >
                  ← Back to homepage
                </Link>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="mt-10 rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_20px_60px_-30px_rgba(30,27,75,0.2)]"
                data-testid="optout-form"
              >
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Your email
                  </span>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="optout-email-input"
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-[#1e1b4b] outline-none transition-colors focus:border-[#1e1b4b]/40 focus:ring-2 focus:ring-[#1e1b4b]/10"
                  />
                </label>

                <label className="mt-5 block">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Reason (optional)
                  </span>
                  <textarea
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Tell us why if you'd like — optional"
                    data-testid="optout-reason-input"
                    className="mt-2 w-full resize-none rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-[#1e1b4b] outline-none transition-colors focus:border-[#1e1b4b]/40 focus:ring-2 focus:ring-[#1e1b4b]/10"
                  />
                </label>

                <label className="mt-5 flex items-start gap-3 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                    data-testid="optout-consent-checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1e1b4b] focus:ring-[#1e1b4b]"
                  />
                  <span>
                    I confirm I am the owner of this email address and request
                    removal from BuyerBeacon Pro's marketing database under
                    applicable privacy laws (CCPA/CPRA, GDPR, and similar).
                  </span>
                </label>

                {error && (
                  <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!email || !agree || submitting}
                  data-testid="optout-submit-button"
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#1e1b4b] px-5 py-3 text-sm font-medium text-white transition-all hover:bg-[#1e1b4b]/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? "Submitting…" : "Submit opt-out request"}
                </button>

                <p className="mt-5 text-center text-[11px] text-slate-500">
                  Need to remove an email from a specific brand's marketing?
                  Use the "unsubscribe" link in that brand's email instead.
                </p>
              </form>
            )}

            <div className="mt-10 rounded-xl border border-slate-200 bg-slate-50/60 p-5 text-sm text-slate-600">
              <p>
                <strong className="text-[#1e1b4b]">California &amp; other state residents:</strong>{" "}
                this form serves as your "Do Not Sell or Share My Personal
                Information" request under the CCPA/CPRA and applicable state
                privacy laws. See our{" "}
                <Link
                  to="/privacy"
                  className="text-[#1e1b4b] underline-offset-4 hover:underline"
                >
                  Privacy Policy
                </Link>{" "}
                for the full description of your rights.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
